/* Casa: visual layer for the OCU (One Click Upsell) pre-purchase popups (Elie's pick A, 5 Oct 2026).
   OCU renders dialog.ocu-popup into a div it appends to <body>, with inline styles; casa-ocu.css
   restyles it. This script adds what CSS cannot: the brand copy, the 30cm→50cm grow animation and
   to-scale size drawing on mirror upgrades, a clear "You save" badge, verified feature chips, style
   swatches on the Bubble Girls offer, and the price on the main button.
   Every step is optional: if OCU's markup or the cart doesn't give us what we need, that piece is
   skipped and the CSS-styled popup still works. OCU owns all buttons and form state; swatches only
   drive OCU's own <select>. Re-applied after OCU re-renders (variant change). */
(() => {
  window.casa = window.casa || {};
  if (window.casa.ocuVisuals) return;
  window.casa.ocuVisuals = true;

  // Verified product facts (product page descriptions, 5-6 Oct 2026). Only these handles get feature chips.
  const MIRROR = ['1M+ colours', '360+ effects', 'Remote + app', 'Alexa · Google · Siri', '2-year guarantee'];
  const CHIPS = {
    'infinity-octamirror': MIRROR,
    'infinity-hexamirror': MIRROR,
    'infinity-trimirror': MIRROR,
    'infinity-quadramirror': MIRROR,
    'infinity-360-mirror': MIRROR,
    'bubble-girls-lamp-collection': ['Hand-painted', 'Up to 12h cordless', 'Touch dimming · 3 levels', 'Glass bubble'],
    'soulful-bubble-girls-lamp-collection': ['Up to 12h cordless', 'Touch dimming · 3 levels', 'Glass bubble'],
    'parisian-sister-lamps-clear-glass': ['Hand-painted', 'Up to 12h cordless', 'Touch dimming', 'Clear glass bubble'],
    'parisian-bubble-girls-colored-glass': ['Hand-painted', 'Up to 12h cordless', 'Touch dimming', 'Colourful glass bubble'],
  };
  const SHAPES = [[/octa/i, 8], [/hexa/i, 6], [/quadra/i, 4], [/tri/i, 3], [/360/, 0]];
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MARK = 'data-casa-ocu';

  let cartPromise = null;
  const cart = () => (cartPromise ||= fetch('/cart.js', { headers: { accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null));
  const products = new Map();
  const product = (handle) => {
    if (!products.has(handle)) {
      products.set(handle, fetch(`/products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
    }
    return products.get(handle);
  };

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    n.setAttribute(MARK, '');
    return n;
  };
  const txt = (root, sel) => (root.querySelector(sel)?.textContent || '').replace(/\s+/g, ' ').trim();

  // Money: parse "$1,234.56", "€575,95", "17.501.000₫"; format a value like a sample string.
  function parseMoney(s) {
    const m = s.match(/\d[\d.,\s]*\d|\d/);
    if (!m) return null;
    const raw = m[0].replace(/\s/g, '');
    const dec = /[.,]\d{2}$/.test(raw) ? 2 : 0;
    return { value: Number(raw.replace(/[.,]/g, '')) / 10 ** dec, dec, raw, sample: s };
  }
  function formatLike(p, value) {
    const sepT = p.raw.match(/\d([.,])\d{3}(?:[.,]|$)/)?.[1] || (p.dec ? (p.raw.at(-3) === ',' ? '.' : ',') : '.');
    const sepD = p.dec ? p.raw.at(-3) : '';
    const fixed = value.toFixed(p.dec);
    const [i, f] = fixed.split('.');
    const body = i.replace(/\B(?=(\d{3})+(?!\d))/g, sepT) + (p.dec ? sepD + f : '');
    return p.sample.replace(p.raw, body).trim();
  }

  // Shapes drawn to scale (viewBox 100x100; regular polygon or circle).
  function shapeSvg(sides, cls) {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', cls);
    let s;
    if (!sides) {
      s = document.createElementNS(ns, 'circle');
      s.setAttribute('cx', 50); s.setAttribute('cy', 50); s.setAttribute('r', 49);
    } else {
      s = document.createElementNS(ns, 'polygon');
      const off = sides === 8 ? Math.PI / 8 : sides === 4 ? Math.PI / 4 : -Math.PI / 2;
      const pts = [];
      for (let k = 0; k < sides; k++) {
        const a = off + (k * 2 * Math.PI) / sides;
        pts.push(`${(50 + 49 * Math.cos(a)).toFixed(2)},${(50 + 49 * Math.sin(a)).toFixed(2)}`);
      }
      s.setAttribute('points', pts.join(' '));
    }
    s.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.append(s);
    return svg;
  }

  // "19.7\" / 50cm" -> {inch:'19.7″', cm:50}
  function size(str) {
    const cm = str.match(/(\d+(?:\.\d+)?)\s*cm/i);
    const inch = str.match(/(\d+(?:\.\d+)?)\s*(?:"|″|in\b)/i);
    return cm ? { cm: Number(cm[1]), inch: inch ? `${inch[1]}″` : '' } : null;
  }

  // OCU rounds its popup price to the nearest cent, but the cart rounds the discount down
  // ($99.99 at 40% off: popup $59.99, cart $60.00). Show the price the cart will charge.
  function savings(pop) {
    const was = parseMoney(txt(pop, '.ocu-totals__was .money') || txt(pop, '.ocu-totals__was'));
    const nowEl = pop.querySelector('.ocu-totals__has .money') || pop.querySelector('.ocu-totals__has');
    const now = parseMoney((nowEl?.textContent || '').trim());
    if (!was || !now || was.value <= now.value) return null;
    const pct = Math.round((1 - now.value / was.value) * 100);
    const unit = 10 ** now.dec;
    const off = Math.floor(Math.round(was.value * unit) * pct / 100) / unit;
    const charged = formatLike(now, was.value - off);
    if (nowEl.children.length === 0 && nowEl.textContent.trim() !== charged) nowEl.textContent = charged;
    return { pct, save: formatLike(now, off), now: charged, value: was.value - off, money: now };
  }

  // Copy on OCU's own labels (timer, decline). Only the text changes; OCU keeps the buttons.
  function setLabel(pop, sel, text) {
    const spans = pop.querySelectorAll(`${sel} span`);
    const span = spans[spans.length - 1];
    if (span && span.children.length === 0 && span.textContent !== text) span.textContent = text;
  }

  function addSaveBadge(pop, s) {
    const holder = pop.querySelector('.ocu-hero-content__totals');
    if (!holder || holder.querySelector('.casa-ocu-save')) return;
    const b = el('span', 'casa-ocu-save');
    b.append(el('i', null, `−${s.pct}%`), el('span', null, `You save ${s.save}`));
    holder.append(b);
    pop.classList.add('casa-ocu--save');
  }

  function addCtaPrice(pop, s) {
    const btn = pop.querySelector('.ocu-cta__buy');
    if (!btn) return;
    let tag = btn.querySelector('.casa-ocu-ctaprice');
    if (!tag) {
      tag = el('span', 'casa-ocu-ctaprice');
      (btn.querySelector('p') || btn).append(tag);
    }
    if (tag.textContent !== s.now) tag.textContent = s.now;
  }

  function addChips(pop, handle) {
    const list = CHIPS[handle];
    const after = pop.querySelector('.ocu-hero-content__totals');
    if (!list || !after || pop.querySelector('.casa-ocu-chips')) return;
    const wrap = el('ul', 'casa-ocu-chips');
    wrap.setAttribute('aria-label', 'Highlights');
    list.forEach((c) => wrap.append(el('li', /colours$/.test(c) ? 'is-colours' : null, c)));
    after.after(wrap);
    pop.classList.add('casa-ocu--chips');
  }

  // head = [lead, accent]: the accent (the saving) gets the brand italic gradient.
  function setCopy(pop, [lead, accent], sub) {
    // The visible headline: OCU also renders a hidden in-cart title that casa-ocu.css hides.
    const p = pop.querySelector('.ocu-headline--timer p, .ocu-modal__title--upgrade p');
    if (!p || p.querySelector('.casa-ocu-head')) return;
    const h = el('span', 'casa-ocu-head', `${lead} `);
    h.append(el('em', null, accent));
    p.replaceChildren(h, el('span', 'casa-ocu-sub', sub));
    pop.classList.add('casa-ocu--copy');
  }

  async function upgrade(pop) {
    const s = savings(pop);
    const name = txt(pop, '.ocu-image__name');
    const select = pop.querySelector('select.selector__dropdown');
    const to = size(select?.value || txt(pop, '.ocu-description-content'));
    const c = await cart();
    // A cart can hold two sizes of the same mirror (70cm + 30cm): compare against the biggest
    // size still below the offer, the one this upgrade steps up from.
    const lines = (c?.items || [])
      .filter((i) => name && i.product_title.toLowerCase().includes(name.toLowerCase()))
      .map((i) => ({ i, cm: size(i.variant_title || '')?.cm || 0 }));
    const below = to ? lines.filter((l) => l.cm && l.cm < to.cm) : [];
    const line = (below.length ? below.reduce((a, b) => (b.cm > a.cm ? b : a)) : lines[0])?.i;
    const from = line && size(line.variant_title || '');
    if (s) { addSaveBadge(pop, s); addCtaPrice(pop, s); }
    if (!to || !from || from.cm >= to.cm) return;
    setLabel(pop, '.ocu-cta__decline', `No thanks, keep the ${from.cm}cm`);
    // OCU names the cart line, not the offer. If the offer image isn't one of the cart product's
    // images, the offer is another product: keep OCU's own offer title and skip our same-shape copy.
    const src0 = pop.querySelector('.ocu-image__large')?.currentSrc || pop.querySelector('.ocu-image__large')?.src || '';
    const file = (u) => (u || '').split('?')[0].split('/').pop().replace(/_\d+x\d*(?=\.)/, '');
    const own = await product(line.handle);
    if (own && src0 && !(own.images || []).some((u) => file(u) === file(src0))) return;
    const shortName = name.replace(/^Infinity\s+/i, '');
    const glow = (to.cm / from.cm) ** 2;
    // Why it matters: how much more light, and what the step up really costs over the cart line.
    const extra = s && line.final_price != null ? s.value - line.final_price / 100 : 0;
    const why = extra > 0 ? ` ${glow.toFixed(1)}× the glow for ${formatLike(s.money, extra)} more.` : ` ${glow.toFixed(1)}× the glow.`;
    if (s) setCopy(pop, ['Go bigger.', `Save ${s.pct}%.`], `Swap your ${from.cm}cm for the ${to.cm}cm ${shortName}:${why}`);
    addChips(pop, line.handle);

    const hero = pop.querySelector('.ocu-modal__hero-image');
    const src = pop.querySelector('.ocu-image__large')?.currentSrc || pop.querySelector('.ocu-image__large')?.src;
    if (!hero || !src || hero.querySelector('.casa-ocu-grow')) return;
    const sides = (SHAPES.find(([re]) => re.test(name)) || [null, 0])[1];
    const ratio = from.cm / to.cm;

    const grow = el('div', 'casa-ocu-grow');
    grow.style.setProperty('--casa-ocu-from', ratio.toFixed(3));
    const img = el('img');
    img.src = src;
    img.alt = `${name} growing from ${from.cm}cm to ${to.cm}cm`;
    img.decoding = 'async';
    const stage = el('div', 'casa-ocu-grow__img');
    stage.append(img);
    const ghost = el('div', 'casa-ocu-grow__ghost');
    ghost.style.inset = `${((1 - ratio) / 2) * 100}%`;
    ghost.append(shapeSvg(sides, 'casa-ocu-shape'));
    const lab = (sz, tag, word) => {
      const l = el('span', `casa-ocu-grow__lab casa-ocu-grow__lab--${tag}`, `${sz.inch} · ${sz.cm}cm`);
      l.append(el('span', 'casa-ocu-grow__word', ` · ${word}`));
      return l;
    };
    const labFrom = lab(from, 'from', 'yours');
    const labTo = lab(to, 'to', 'upgrade');
    grow.append(stage, ghost, labFrom, labTo);

    const rep = el('p', 'casa-ocu-replaces');
    rep.append(el('span', null, 'Replaces '), el('b', null, `${name} ${from.cm}cm`));

    const dims = el('div', 'casa-ocu-dims');
    dims.style.setProperty('--casa-ocu-r', ratio.toFixed(3));
    for (const [sz, cls, label] of [[from, 'from', 'In your cart'], [to, 'to', 'Upgrade']]) {
      const col = el('div', `casa-ocu-dims__col casa-ocu-dims__col--${cls}`);
      const box = el('div', 'casa-ocu-dims__shape');
      box.append(shapeSvg(sides, 'casa-ocu-shape'));
      const w = el('div', 'casa-ocu-dims__w');
      w.append(el('b', null, sz.inch || `${sz.cm}cm`), el('small', null, sz.inch ? ` ${sz.cm}cm` : ''));
      col.append(box, el('div', 'casa-ocu-dims__ruler'), w, el('span', 'casa-ocu-dims__lab', label));
      dims.append(col);
    }
    const note = el('p', 'casa-ocu-dims__note');
    note.append(el('b', null, `${glow.toFixed(1)}×`), el('span', null, 'the glow'));
    dims.append(note);

    hero.prepend(grow, rep);
    pop.querySelector('.ocu-hero-content__totals')?.after(dims);
    pop.classList.add('casa-ocu--upgrade');
    if (!reduceMotion) requestAnimationFrame(() => requestAnimationFrame(() => grow.classList.add('is-grown')));
    else grow.classList.add('is-grown', 'is-still');
  }

  async function sameAgain(pop) {
    const s = savings(pop);
    if (s) { addSaveBadge(pop, s); addCtaPrice(pop, s); }
    const select = pop.querySelector('select.selector__dropdown');
    const options = select ? [...select.options].map((o) => o.value) : [];
    const title = txt(pop, '.ocu-hero-title');
    const one = title && title.length <= 24 ? title.replace(/s$/, '') : 'one';
    if (s) setCopy(pop, [`Add a second ${one}.`, `Save ${s.pct}%.`], options.length > 1 ? `Pick any style. This ${s.pct}% off is only in this popup.` : `This ${s.pct}% off is only in this popup.`);
    setLabel(pop, '.ocu-cta__decline', 'No thanks, just one');
    // OCU holds the Add to cart until the shopper answers, so the cart can still be empty here:
    // use the cart line if there is one, otherwise the product page the popup opened on.
    const hasOptions = (p) => p && options.length && options.every((v) => p.variants.some((x) => x.options?.includes(v)));
    const c = await cart();
    let handle = c?.items?.find((i) => (i.options_with_values || []).some((o) => options.includes(o.value)))?.handle;
    if (!handle) {
      const m = location.pathname.match(/\/products\/([^/?#]+)/);
      if (m && hasOptions(await product(m[1]))) handle = m[1];
    }
    if (!handle) return;
    addChips(pop, handle);
    if (!select || options.length < 2 || pop.querySelector('.casa-ocu-swatches')) return;
    const p = await product(handle);
    if (!p) return;
    const imgFor = (v) => p.variants.find((x) => x.title === v || x.options?.includes(v))?.featured_image?.src;
    if (!options.every(imgFor)) return;
    const row = el('div', 'casa-ocu-swatches');
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', select.closest('.selector__wrapper')?.querySelector('label')?.textContent?.trim() || 'Style');
    options.forEach((v) => {
      const b = el('button', 'casa-ocu-swatch');
      b.type = 'button';
      b.setAttribute('aria-pressed', String(select.value === v));
      const im = el('img');
      im.src = `${imgFor(v)}${imgFor(v).includes('?') ? '&' : '?'}width=160`;
      im.alt = '';
      im.loading = 'lazy';
      b.append(im, el('span', null, v));
      b.addEventListener('click', () => {
        select.value = v;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        select.dispatchEvent(new Event('input', { bubbles: true }));
        row.querySelectorAll('.casa-ocu-swatch').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      });
      row.append(b);
    });
    select.addEventListener('change', () => row.querySelectorAll('.casa-ocu-swatch').forEach((x) => x.setAttribute('aria-pressed', String(x.lastChild.textContent === select.value))));
    (pop.querySelector('.ocu-modal__incart-image') || pop.querySelector('.ocu-hero-content__totals'))?.append(row);
    pop.classList.add('casa-ocu--swatches');
  }

  // One run at a time per popup: both builders await network data, and two overlapping runs
  // would each pass their "already added?" checks and insert duplicates.
  const running = new WeakMap();
  function enhance(pop) {
    if (running.get(pop)) { running.set(pop, 'again'); return; }
    running.set(pop, true);
    setLabel(pop, '.ocu-offer__timer', 'Price held for');
    const job = pop.querySelector('.ocu-modal__title--upgrade, .ocu-hero__replace') ? upgrade(pop) : sameAgain(pop);
    job.catch(() => {}).finally(() => {
      const again = running.get(pop) === 'again';
      running.delete(pop);
      if (again) enhance(pop);
    });
  }

  // Mutations that only inserted our own nodes don't need another pass. An attribute change
  // (the dialog opening) has no added nodes and must still count.
  const ours = (m) => m.type === 'childList' && m.addedNodes.length > 0
    && [...m.addedNodes].every((n) => n.nodeType === 1 && n.hasAttribute(MARK));

  const watched = new WeakSet();
  function watch(pop) {
    if (watched.has(pop)) return;
    watched.add(pop);
    let wasOpen = false;
    const run = () => {
      const open = pop.open || pop.classList.contains('ocu-popup--live');
      if (open && !wasOpen) cartPromise = null; // a new opening: the cart may have changed
      wasOpen = open;
      if (open) enhance(pop);
    };
    // Run straight from the observer (already batched) rather than on the next frame:
    // requestAnimationFrame never fires in a tab that isn't painting.
    new MutationObserver((ms) => { if (!ms.every(ours)) run(); })
      .observe(pop, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    run();
  }

  // OCU can append its container to <body> first and insert the dialog into it later, so watch
  // the whole body tree, but only scan when an added node is or holds a popup.
  const isPopupNode = (n) => n.nodeType === 1 && (n.matches('.ocu-popup') || n.querySelector('.ocu-popup'));
  const scan = () => document.querySelectorAll('dialog.ocu-popup, .ocu-popup').forEach(watch);
  new MutationObserver((ms) => {
    if (ms.some((m) => [...m.addedNodes].some(isPopupNode))) scan();
  }).observe(document.body, { childList: true, subtree: true });
  scan();
})();
